import { apiClient } from '../utils/api-client';
import type { EntryOptions, TurnSeats } from '../types/trophy';
// Shared so the server's error code survives the throw: the list is refused
// with `dpa_required` until the circuit signs, and the page routes on that.
import { throwForResponse } from './api-error';

/** The gender of the class somebody competes in. */
export type EntryGender = 'male' | 'female';

/** One athlete's entry to one stage. */
export interface Registration {
  id: string;
  name?: string | null;
  surname?: string | null;
  full_name?: string | null;
  email?: string | null;
  paid: boolean;
  paid_at?: string | null;
  /** Confirmed by the desk: the person is climbing, and may be scored. */
  confirmed: boolean;
  confirmed_at?: string | null;
  category_id?: string | null;
  category_name?: string | null;
  /**
   * Somebody chose this class rather than the organiser's strategy giving it
   * for the row's date and gender. The form shows such a class as picked, and
   * one the strategy gave as "automatic".
   */
  category_chosen?: boolean;
  /** The gender of the class; null where nobody gave it, or the agreement does not cover it. */
  gender?: EntryGender | null;
  /** The turn booked, when the class is split over several bookable rounds. */
  competition_round_id?: string | null;
  competition_round_name?: string | null;
  /** Split over two or more turns and none booked: on every one until somebody books it. */
  turn_to_choose?: boolean;
  user_slug?: string | null;
  user_username?: string | null;
  birth_date?: string | null;
  /**
   * Under 14 by the date of birth. The letter goes to the guardian, not to
   * them: below the consent age the consent is a parent's.
   */
  minor?: boolean;
  /** The parent's or guardian's address, on an entrant under 14. */
  guardian_email?: string | null;
  /** The guardian has opened the child's managed account. Never with a `user_slug`. */
  managed?: boolean;
  team_id?: string | null;
  team_name?: string | null;
  /**
   * Typed in by a squad's team manager from the competition's page rather
   * than at the desk: the row that may carry no address at all.
   */
  team_entered_at?: string | null;
  /** Who did it — gone once that account is. */
  team_entered_by?: string | null;
  /**
   * Picked by the squad from its athletes at another stage of the circuit
   * rather than typed in: the details came from that entry, and the squad was
   * never shown them.
   */
  team_reentered?: boolean;
  /** The named guardian answered "this is not my child". */
  guardian_declined?: boolean;
  /** When the letter about this entry went out, if it has. */
  invited_at?: string | null;
  /** Whether this viewer may still work the desk here. */
  editable?: boolean;
  created_at: string;
}

/**
 * Somebody who could be entered without being typed in: already entered
 * elsewhere in this circuit, or on the hosting gym's roster, and not here yet.
 * `id` is that entry's or that roster row's — picking them copies its details
 * forward.
 */
export interface RegistrationCandidate {
  id: number;
  /**
   * Where the row comes from — an entry at another stage of the circuit, or a
   * row on the hosting gym's roster — which is which parameter enters them.
   */
  kind: 'entry' | 'member';
  full_name: string;
  email?: string | null;
  /**
   * Where the row says this person comes from: the squad they are entered for
   * at another stage, or — off a roster — the gym whose it is.
   */
  affiliation?: string | null;
  category_name?: string | null;
  /** Under 14: entered on a guardian's address, possibly with none of their own. */
  minor?: boolean;
}

/** One of a class's turns, as the header above its table offers it. */
export interface RegistrationTurn extends TurnSeats {
  id: string;
  name: string;
}

export interface RegistrationCategory {
  id: string;
  name: string;
  /**
   * The turns the class may book, where the desk books one: two or more, and
   * the organiser's agreement naming the booking. Empty otherwise.
   */
  turns?: RegistrationTurn[];
}

export interface RegistrationPage {
  registrations: Registration[];
  total: number;
  paid: number;
  confirmed: number;
  /** The circuit's categories, sent alongside so every row's picker has them. */
  categories: RegistrationCategory[];
  /** The circuit's squads, sent alongside for the same reason. */
  teams: RegistrationTeam[];
  /** Whether this viewer may still add and change entries at all. */
  deskOpen: boolean;
  /** Whether the competition takes an entry fee: without one, no "paid" anywhere. */
  acceptsPayments: boolean;
  /** Whether an entry here may hold the gender of its class: the organiser's agreement covers it. */
  genderCollected: boolean;
  /** Whether an entry here may book a turn: the organiser's agreement names it. */
  turnsCollected: boolean;
  /** Some class here is split over two or more turns an entry may book. */
  turnsOffered: boolean;
  /** A category left blank is the organiser's strategy's to give, rather than none. */
  assignsCategories: boolean;
}

/** One of the circuit's squads. */
export interface RegistrationTeam {
  id: string;
  name: string;
}

/**
 * What the entry form sends. An entry stands on its own — there is no roster row
 * behind it unless it was picked off one, in which case the identity is stamped
 * server-side and these fields are ignored.
 */
export interface EntryPayload {
  name: string;
  surname?: string | null;
  email?: string | null;
  /** Required when the date of birth is under 14; ignored otherwise. */
  guardian_email?: string | null;
  birth_date?: string | null;
  /**
   * Blank, where the organiser picked a strategy, is "automatic": the class it
   * gives for the date and the gender. Without one, blank is none.
   */
  competition_category_id?: string | null;
  competition_team_id?: string | null;
  /** Sent only where the agreement covers it; blank clears it. */
  gender?: EntryGender | '' | null;
  /**
   * Sent only where the class has turns to choose from: a round's id, or blank
   * for "to choose". Left out, the booking stays as it is.
   */
  competition_round_id?: string | null;
}

/**
 * One row of a spreadsheet, as the import sends it. `row` is the sheet's own
 * row number, so an error can point at the line the desk is looking at. The
 * category and squad go by name — the only thing a file typed by a club can
 * carry — and an `id` (as the export writes it) says which entry to correct.
 * A key left out means the sheet had no such column.
 */
export interface ImportRow {
  row: number;
  id?: string;
  name?: string;
  surname?: string;
  email?: string;
  guardian_email?: string;
  category?: string;
  team?: string;
  birth_date?: string;
  /** As the sheet spells it ("M", "Femminile"…): the server reads it. */
  gender?: string;
}

/** One thing wrong with one row of an import, as the server reports it. */
export interface ImportRowError {
  row: number | null;
  field?: string | null;
  code?: string;
  message: string;
}

export interface ImportResult {
  /** Every entry the file touched, in the file's order. */
  registrations: Registration[];
  created: number;
  updated: number;
}

/**
 * The server refused the whole file. Nothing was written; `rows` says why,
 * one line per problem, so the desk can fix the sheet and send it again.
 */
export class ImportRefusedError extends Error {
  rows: ImportRowError[];

  constructor(rows: ImportRowError[], message: string) {
    super(message);
    this.name = 'ImportRefusedError';
    this.rows = rows;
  }
}

/**
 * What the header above a table sets on everyone ticked below it. Narrower than
 * the entry form on purpose — the server permits exactly these four, because an
 * identity belongs to one person and is never written to a batch.
 */
export interface BulkPayload {
  competition_category_id?: string | null;
  competition_team_id?: string | null;
  paid?: boolean;
  confirmed?: boolean;
  /** A turn of the rows' class, or null: back to "turn to choose". */
  competition_round_id?: string | null;
}

interface RegistrationResponse {
  data: { id: string; type: 'registration'; attributes: Registration };
}

interface RegistrationsListResponse {
  data: { id: string; type: 'registration'; attributes: Registration }[];
  meta?: {
    total?: number;
    paid?: number;
    confirmed?: number;
    categories?: RegistrationCategory[];
    teams?: RegistrationTeam[];
    desk_open?: boolean;
    accepts_payments?: boolean;
    gender_collected?: boolean;
    turns_collected?: boolean;
    turns_offered?: boolean;
    assigns_categories?: boolean;
  };
}

interface CandidatesListResponse {
  data: { id: string; type: 'registration_candidate'; attributes: RegistrationCandidate }[];
}

/**
 * The entry list of one stage, drawn from its circuit's roster. Entrants are
 * added by roster id — the identity is stamped server-side, so a client can
 * never enter somebody under a name the circuit never collected.
 */
class RegistrationsService {
  async list(stageSlug: string): Promise<RegistrationPage> {
    const response = await apiClient.get(`/api/competitions/${stageSlug}/registrations`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the entry list');
    }

    const data: RegistrationsListResponse = await response.json();
    return {
      registrations: data.data.map((item) => item.attributes),
      total: data.meta?.total ?? data.data.length,
      paid: data.meta?.paid ?? 0,
      confirmed: data.meta?.confirmed ?? 0,
      categories: data.meta?.categories ?? [],
      teams: data.meta?.teams ?? [],
      deskOpen: data.meta?.desk_open ?? false,
      acceptsPayments: data.meta?.accepts_payments ?? false,
      genderCollected: data.meta?.gender_collected ?? false,
      turnsCollected: data.meta?.turns_collected ?? false,
      turnsOffered: data.meta?.turns_offered ?? false,
      assignsCategories: data.meta?.assigns_categories ?? false,
    };
  }

  /**
   * The category and the rounds the entry form's values lead to — the line
   * under its category list, and the turns it may book. The desk's own copy of
   * the public `entry_options`: it answers before the competition is public.
   * `category_id` is a class picked from the list, kept as it is.
   */
  async entryOptions(
    stageSlug: string,
    details: { gender?: string | null; birth_date?: string | null; category_id?: string | null }
  ): Promise<EntryOptions> {
    const response = await apiClient.post(`/api/competitions/${stageSlug}/registrations/entry_options`, {
      gender: details.gender ?? null,
      birth_date: details.birth_date ?? null,
      ...(details.category_id ? { category_id: details.category_id } : {}),
    });

    if (!response.ok) {
      await throwForResponse(response, 'Could not read the categories');
    }

    const data: { data: EntryOptions } = await response.json();
    return data.data;
  }

  /**
   * Who has done another stage of this circuit and is not entered here yet.
   * Picking one prefills the form from that entry.
   */
  async candidates(stageSlug: string, search?: string): Promise<RegistrationCandidate[]> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await apiClient.get(
      `/api/competitions/${stageSlug}/registrations/candidates${query}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to search the roster');
    }

    const data: CandidatesListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  /**
   * Enters somebody: typed in, carried forward from another stage of the
   * circuit (`from_entry_id`), or picked off the hosting gym's roster
   * (`from_member_id`) — either id prefills the entry. One call either way,
   * and the person is emailed that they are in.
   */
  async create(
    stageSlug: string,
    attributes: Partial<EntryPayload> & { from_entry_id?: number; from_member_id?: number; paid?: boolean }
  ): Promise<Registration> {
    const response = await apiClient.post(`/api/competitions/${stageSlug}/registrations`, {
      data: { attributes },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to add the entrant');
    }

    const data: RegistrationResponse = await response.json();
    return data.data.attributes;
  }

  /** Everything on an entry is this competition's own, so all of it is editable. */
  async update(
    stageSlug: string,
    id: string,
    attributes: Partial<EntryPayload> & { paid?: boolean; confirmed?: boolean }
  ): Promise<Registration> {
    const response = await apiClient.patch(
      `/api/competitions/${stageSlug}/registrations/${id}`,
      { data: { attributes } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the entry');
    }

    const data: RegistrationResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * The same change on every ticked row, in one request. All of it or none:
   * the server applies them in a transaction, so a refusal leaves the list as
   * it was rather than half-sorted.
   */
  async bulkUpdate(
    stageSlug: string,
    ids: string[],
    attributes: BulkPayload
  ): Promise<Registration[]> {
    const response = await apiClient.patch(
      `/api/competitions/${stageSlug}/registrations/bulk_update`,
      { data: { attributes: { ids, ...attributes } } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the entries');
    }

    const data: RegistrationsListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  /**
   * A spreadsheet's rows, already read off the sheet, in one request. Rows
   * naming an entry (by id, then by email) correct it; the rest are entered
   * and emailed. All of it or none of it: a refusal comes back as an
   * `ImportRefusedError` carrying every row that stopped it.
   */
  async import(stageSlug: string, rows: ImportRow[]): Promise<ImportResult> {
    const response = await apiClient.post(`/api/competitions/${stageSlug}/registrations/import`, {
      data: { attributes: { rows } },
    });

    if (response.status === 422) {
      const body = (await response.json().catch(() => ({}))) as {
        errors?: { detail: string; code?: string; meta?: { row?: number; field?: string } }[];
      };
      const problems = (body.errors ?? []).map((error) => ({
        row: error.meta?.row ?? null,
        field: error.meta?.field ?? null,
        code: error.code,
        message: error.detail,
      }));
      throw new ImportRefusedError(problems, problems[0]?.message ?? 'The file was refused');
    }

    if (!response.ok) {
      await throwForResponse(response, 'Failed to import the entries');
    }

    const data: RegistrationsListResponse & { meta?: { created?: number; updated?: number } } =
      await response.json();
    return {
      registrations: data.data.map((item) => item.attributes),
      created: data.meta?.created ?? 0,
      updated: data.meta?.updated ?? 0,
    };
  }

  /**
   * Sends this entry's letter again: the athlete's, or — under 14 — the
   * guardian's. Asked for on purpose, so the "once per circuit" quiet rules
   * do not apply.
   */
  async resendInvitation(stageSlug: string, id: string): Promise<Registration> {
    const response = await apiClient.post(
      `/api/competitions/${stageSlug}/registrations/${id}/resend_invitation`,
      {}
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to send the invitation again');
    }

    const data: RegistrationResponse = await response.json();
    return data.data.attributes;
  }

  async remove(stageSlug: string, id: string): Promise<void> {
    const response = await apiClient.delete(`/api/competitions/${stageSlug}/registrations/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to withdraw the entry');
    }
  }
}

export const registrationsService = new RegistrationsService();

